import React, { useEffect, useState } from "react";
import { parse } from "query-string";
import { Container } from "@sainsburys-tech/luna-grid";

import { missionId as pathMissionId, getLoginLink } from "utils/urls";

// sessionStorage keys shared with Routes.js
const OAUTH2_REDIRECT_GUARD = "oauth2_flow_initiated";

/**
 * Clear all session state that would prevent a fresh OAuth2 flow from starting,
 * then navigate to the login page.  Routes.js will call initiateOAuth2Flow()
 * because both hasFreshChallenge and hasStoredTokens will be false.
 */
const returnToLogin = (mission, loginLink) => (e) => {
  e && e.preventDefault();
  // Remove the stored tokens so hasStoredTokens is false on the login page.
  sessionStorage.removeItem(`${mission}_tokens`);
  // Remove the redirect-loop guard so initiateOAuth2Flow is not blocked.
  sessionStorage.removeItem(OAUTH2_REDIRECT_GUARD);
  window.location.href = loginLink;
};

/**
 * OAuth2 Authorization Code callback page.
 *
 * Hydra redirects the browser here after the user has authenticated and
 * consented.  The URL contains `?code=...&missionId=...&state=...`.
 *
 * On error (e.g. CSRF failure), Hydra redirects here with
 * `?error=...&error_description=...` instead of a code.
 *
 * This page exchanges the authorization code for tokens via Hydra's
 * /oauth2/token endpoint (proxied through nginx — no client secret needed
 * because the Hydra client is registered with token_endpoint_auth_method=none).
 *
 * After a successful exchange the user is redirected to the id-account-ui
 * myaccount SPA (REACT_APP_ACCOUNT_UI_BASE_URL/sainsburys/ui/<mission>).
 * If REACT_APP_ACCOUNT_UI_BASE_URL is not set (local dev), falls back to
 * staying on the login-ui origin.
 */
const CallbackPage = () => {
  const [status, setStatus] = useState("exchanging"); // "exchanging" | "success" | "error"
  const [errorMessage, setErrorMessage] = useState("");
  const [missionName, setMissionName] = useState("");

  useEffect(() => {
    const qs = parse(window.location.search);
    const code = qs.code;
    // Prefer the mission extracted from the URL path (/login-ui/<mission>/callback)
    // over the legacy ?missionId= query param.  The path-based form is required so
    // nginx routes the Hydra redirect to the React SPA (via the mission-prefix rule)
    // rather than to id-account-auth via the catch-all.
    const mission = pathMissionId || qs.missionId || "myaccount";
    setMissionName(mission);
    // Must match the redirect_uri sent in initiateOAuth2Flow and registered in Hydra.
    const redirectUri = `${window.location.origin}/${mission}/callback`;

    // ── Hydra error redirect ──────────────────────────────────────────────────
    // When Hydra cannot complete the flow (e.g. CSRF session cookie missing,
    // challenge expired) it redirects here with ?error=...&error_description=...
    // instead of a code.  The most common cause in this architecture is
    // REACT_APP_HYDRA_OAUTH_HOST not being injected, so the initial /oauth2/auth
    // call went through the nginx proxy instead of directly to Hydra — the
    // browser never stored the Hydra-domain _csrf cookie, so the login_verifier
    // round-trip fails with "No CSRF value available in the session cookie".
    //
    // Recovery: clear all OAuth2 session state so that the next login page load
    // calls initiateOAuth2Flow() again and obtains a fresh Hydra session.
    if (qs.error) {
      // Clear the redirect-loop guard so the login page can re-initiate.
      sessionStorage.removeItem(OAUTH2_REDIRECT_GUARD);
      sessionStorage.removeItem(`${mission}_tokens`);

      const isCsrfError =
        qs.error === "request_forbidden" ||
        (qs.error_description &&
          qs.error_description.toLowerCase().includes("csrf"));

      if (isCsrfError) {
        // CSRF errors are almost always caused by REACT_APP_HYDRA_OAUTH_HOST not
        // being set, so the initial /oauth2/auth call was proxied through nginx
        // and the browser rejected Hydra's _csrf cookie (domain mismatch).
        // We show a user-friendly message and let the user click "Return to
        // sign-in" (which calls returnToLogin → clears guard → fresh flow).
        // We do NOT auto-redirect to avoid an infinite loop if the root-cause
        // misconfiguration is still present.
        console.warn(
          "OAuth2 CSRF error — Hydra session cookie was absent or invalid.",
          "Most likely cause: REACT_APP_HYDRA_OAUTH_HOST is not set so the",
          "initial /oauth2/auth call went through the nginx proxy instead of",
          "directly to the Hydra domain.",
          qs.error_description
        );
        setStatus("error");
        setErrorMessage(
          "Your sign-in session expired or was interrupted. " +
            "Please click 'Return to sign-in' to try again."
        );
        return;
      }

      setStatus("error");
      setErrorMessage(
        qs.error_description
          ? decodeURIComponent(qs.error_description)
          : `Authorization error: ${qs.error}`
      );
      return;
    }

    if (!code) {
      setStatus("error");
      setErrorMessage("No authorization code found in the callback URL.");
      return;
    }

    const body = new URLSearchParams({
      grant_type: "authorization_code",
      code,
      redirect_uri: redirectUri,
      client_id: mission,
    });

    fetch("/oauth2/token", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    })
      .then((res) => {
        if (!res.ok) {
          return res.text().then((text) => {
            throw new Error(
              `Token exchange failed (HTTP ${res.status}): ${text}`
            );
          });
        }
        return res.json();
      })
      .then((tokens) => {
        // Store tokens for the current session.
        sessionStorage.setItem(`${mission}_tokens`, JSON.stringify(tokens));
        setStatus("success");

        // Determine where to send the user after a successful token exchange.
        //
        // For missions that have their OWN website (e.g. GOL → www.sainsburys.co.uk/gol-ui/),
        // configure REACT_APP_<MISSION>_HOME_URL (e.g. REACT_APP_GOL_HOME_URL).
        // The mission's site will start its own OAuth2 flow; because Hydra has an
        // active session the user will be auto-logged-in without seeing a login form.
        //
        // For account-management missions (e.g. myaccount) that do NOT have a
        // separate home URL configured, fall back to the id-account-ui SPA at
        // REACT_APP_ACCOUNT_UI_BASE_URL/sainsburys/ui/<mission>.
        const missionEnvKey = `REACT_APP_${mission.toUpperCase()}_HOME_URL`;
        const missionHomeUrl =
          (window._env && window._env[missionEnvKey]) ||
          (() => {
            const accountUiBase =
              (window._env && window._env.REACT_APP_ACCOUNT_UI_BASE_URL) ||
              window.location.origin;
            return `${accountUiBase}/sainsburys/ui/${mission}`;
          })();
        // Brief pause so the "Completing sign-in…" message is visible, then navigate.
        setTimeout(() => {
          window.location.href = missionHomeUrl;
        }, 800);
      })
      .catch((err) => {
        console.error("OAuth2 token exchange error:", err);
        setStatus("error");
        setErrorMessage(err.message || "Unknown error during token exchange.");
      });
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginLink = getLoginLink();

  return (
    <Container soft>
      {status === "exchanging" && (
        <p aria-live="polite">Completing sign-in, please wait…</p>
      )}
      {status === "success" && (
        <p aria-live="polite">
          Sign-in successful. Redirecting to your account…
        </p>
      )}
      {status === "error" && (
        <div role="alert">
          <p>There was a problem completing your sign-in.</p>
          {errorMessage && (
            <p>
              <small>{errorMessage}</small>
            </p>
          )}
          <p>
            {/* Same guard-clearing nav so the login page can re-initiate
                the OAuth2 flow and obtain a new login_challenge. */}
            <a href={loginLink} onClick={returnToLogin(missionName, loginLink)}>
              Return to sign-in
            </a>
          </p>
        </div>
      )}
    </Container>
  );
};

export default CallbackPage;
